import type { Order } from '../../../types'
import { CRYPTO_ASSETS } from '../../../lib/constants'
import { formatCrypto, formatDate } from '../../../lib/utils'
import { cx, text, focus, statusStyles } from '../../../lib/ui-primitives'
import { Spinner } from '../../primitives'

// ============================================
// ORDER LIST
// Scrollable list of order cards
// ============================================

export interface OrderListProps {
  orders: Order[]
  selectedOrderId?: string
  onSelectOrder: (order: Order) => void
  loading?: boolean
}

/**
 * OrderList - Scrollable list of order cards
 *
 * Features:
 * - Loading state
 * - Empty state
 * - Selection highlight
 * - Status color coding
 */
export function OrderList({
  orders,
  selectedOrderId,
  onSelectOrder,
  loading = false,
}: OrderListProps) {
  if (loading) {
    return (
      <div className="text-center py-12">
        <Spinner size="md" centered />
        <p className={cx('mt-3', text.tertiary)}>Loading orders...</p>
      </div>
    )
  }

  if (orders.length === 0) {
    return (
      <div className="text-center py-12 bg-white/[0.02] rounded-xl border border-white/10">
        <div className="text-4xl mb-3" aria-hidden="true">📭</div>
        <p className={text.tertiary}>No orders found</p>
      </div>
    )
  }

  return (
    <div
      className="space-y-2 max-h-[600px] overflow-y-auto pr-2 scrollbar-thin"
      role="listbox"
      aria-label="Orders list"
    >
      {orders.map((order) => (
        <OrderCard
          key={order.id}
          order={order}
          selected={selectedOrderId === order.id}
          onClick={() => onSelectOrder(order)}
        />
      ))}
    </div>
  )
}

// ============================================
// ORDER CARD
// Individual order list item
// ============================================

interface OrderCardProps {
  order: Order
  selected: boolean
  onClick: () => void
}

function OrderCard({ order, selected, onClick }: OrderCardProps) {
  const asset = CRYPTO_ASSETS[order.crypto]
  const statusStyle = statusStyles[order.status] || statusStyles.pending

  return (
    <button
      onClick={onClick}
      role="option"
      aria-selected={selected}
      className={cx(
        'w-full text-left p-3 sm:p-4 rounded-xl border transition-all',
        focus.ring,
        selected
          ? 'bg-accent-primary/15 border-accent-primary'
          : 'bg-white/[0.02] border-white/10 hover:bg-white/[0.04] hover:border-white/15'
      )}
    >
      {/* Top row: ID + Status */}
      <div className="flex items-start justify-between mb-2">
        <span className={cx('text-sm font-medium', text.mono)}>{order.id}</span>
        <span className={cx('px-2 py-0.5 rounded text-xs font-medium', statusStyle)}>
          {order.status}
        </span>
      </div>

      {/* Middle row: Customer + Amount */}
      <div className="flex items-center justify-between text-sm">
        <span className={text.secondary}>{order.customer.name}</span>
        <span className={text.mono} style={{ color: asset?.color }}>
          {formatCrypto(order.amountCrypto, 2)} {order.crypto}
        </span>
      </div>

      {/* Bottom row: Date */}
      <div className={cx('text-xs mt-1.5', text.disabled)}>
        {formatDate(order.createdAt)}
      </div>
    </button>
  )
}
